import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { InvitationStatusEnum } from '@shared/hooks/useFetchInviteQrCode';
import { InviteDecision } from './ui/invite-decision';
import { AcceptedInvite } from './ui/accepted-invite';
import { DeclinedInvite } from './ui/declined-invite';
import { FC, useState } from 'react';
import { ErrorInvite } from './ui/error-invite';
import { BlockedInvite } from './ui/blocked-invite';
import { ExpiredInvite } from './ui/expired-invite';
import { ChangeDecision, ChangeDecisionVariant } from './ui/change-decision';
import { useMobile } from '@shared/hooks/useMobile';

type InviteStatusProps = {
  status: InvitationStatusEnum;
  eventName: string;
  dateStart: string;
  dateEnd: string;
  loading?: boolean;
  /** Возвращает, применён ли новый статус: при ошибке запроса подтверждение остаётся открытым. */
  onAccept?: () => Promise<boolean> | void;
  onDecline?: () => Promise<boolean> | void;
  onOpenTicket?: () => void;
  onAddToCalendar?: () => void;
  onRetry?: () => void;
  error?: boolean;
  ticketSent?: boolean;
  inviteAcceptionInfo: string;
};

export const InviteStatus: FC<InviteStatusProps> = ({
  status,
  eventName,
  dateStart,
  dateEnd,
  onAccept,
  onDecline,
  onOpenTicket,
  onAddToCalendar,
  onRetry,
  ticketSent,
  inviteAcceptionInfo,
}) => {
  const [decisionToConfirm, setDecisionToConfirm] = useState<ChangeDecisionVariant | null>(null);
  const [isRespondPending, setIsRespondPending] = useState(false);
  const [hasRespondFailed, setHasRespondFailed] = useState(false);
  const isMobile = useMobile();

  const closeChangeDecision = () => {
    setDecisionToConfirm(null);
    setHasRespondFailed(false);
  };

  // Закрываем окно только когда статус получен: urql на сетевой и graphql-ошибке не бросает
  // исключение, а возвращает результат с error, и закрытие увело бы участника на прежний экран.
  const confirmChangeDecision = async () => {
    setIsRespondPending(true);
    setHasRespondFailed(false);

    try {
      const isApplied = decisionToConfirm === 'accept' ? await onAccept?.() : await onDecline?.();

      if (isApplied) {
        setDecisionToConfirm(null);
      } else {
        setHasRespondFailed(true);
      }
    } catch {
      setHasRespondFailed(true);
    } finally {
      setIsRespondPending(false);
    }
  };

  const changeDecision = decisionToConfirm && (
    <ChangeDecision
      variant={decisionToConfirm}
      layout={isMobile ? 'screen' : 'modal'}
      isPending={isRespondPending}
      hasFailed={hasRespondFailed}
      onConfirm={confirmChangeDecision}
      onCancel={closeChangeDecision}
    />
  );

  // На мобильной подтверждение занимает место экрана статуса, на десктопе лежит поверх него.
  const isStatusScreenReplaced = isMobile && Boolean(decisionToConfirm);

  switch (status) {
    case InvitationStatusEnum.PENDING:
    case InvitationStatusEnum.SENT:
      return (
        <div {...stylex.props(styles.content)}>
          <InviteDecision
            eventName={eventName}
            dateStart={dateStart}
            dateEnd={dateEnd}
            onAccept={onAccept}
            onDecline={onDecline}
          />
        </div>
      );

    case InvitationStatusEnum.ACCEPTED:
      return (
        <div {...stylex.props(styles.content)}>
          {!isStatusScreenReplaced && (
            <AcceptedInvite
              eventName={eventName}
              dateStart={dateStart}
              dateEnd={dateEnd}
              inviteAcceptionInfo={inviteAcceptionInfo}
              onChangeDecision={() => setDecisionToConfirm('decline')}
              onOpenTicket={onOpenTicket}
              onAddToCalendar={onAddToCalendar}
              ticketSent={ticketSent}
            />
          )}
          {changeDecision}
        </div>
      );

    case InvitationStatusEnum.DECLINED:
      return (
        <div {...stylex.props(styles.content)}>
          {!isStatusScreenReplaced && (
            <DeclinedInvite
              eventName={eventName}
              dateStart={dateStart}
              dateEnd={dateEnd}
              onChangeDecision={() => setDecisionToConfirm('accept')}
            />
          )}
          {changeDecision}
        </div>
      );

    case InvitationStatusEnum.BLOCKED:
      return (
        <div {...stylex.props(styles.content)}>
          <BlockedInvite />
        </div>
      );

    case InvitationStatusEnum.EXPIRED:
      return (
        <div {...stylex.props(styles.content)}>
          <ExpiredInvite />
        </div>
      );

    default:
      return (
        <div {...stylex.props(styles.content)}>
          <ErrorInvite onRetry={onRetry} />
        </div>
      );
  }
};
